import { ProfessionalSellerShareholder } from '@/types/api/api-v2';
import { CommonCountryArray, UserDTO } from '@/types/other';
import { ProfessionalSellerDataClient } from '@/types/user/user.types';
import {
  ValidateProfessionalSellerBusinessData,
  ValidateProfessionalSellerPersonalData,
} from '@/types/validation/functions.validations.type';
import {
  validateProfessionalSellerBusinessDataSchema,
  validateProfessionalSellerPersonalDataSchema,
  validateProfessionalSellerShareholderSchema,
} from '@/validations/professional.validations';
import validateUserSchema from '@/validations/user.validations';
import { TFunction } from 'i18next';
import { parsePhoneNumberWithError } from 'libphonenumber-js';

const validateProfessionalSellerPersonalData: ValidateProfessionalSellerPersonalData =
  async (data, tProf) => {
    try {
      await validateProfessionalSellerPersonalDataSchema(tProf).validate(data);
      const phoneNumber = parsePhoneNumberWithError(
        data.primeBusinessContactPhonePrefix + data.primeBusinessContactPhone
      );
      return {
        isValid: true && phoneNumber.isValid(),
        message: null,
      };
    } catch (err: any) {
      return {
        isValid: false,
        message: err.message,
      };
    }
  };

const validateProfessionalSellerBusinessData: ValidateProfessionalSellerBusinessData =
  async (data, tProf) => {
    try {
      await validateProfessionalSellerBusinessDataSchema(
        data.primeBusinessCompanyType === 'singleCompany',
        tProf
      ).validate(data);
      return {
        isValid: true,
        message: null,
      };
    } catch (err: any) {
      return {
        isValid: false,
        message: err.message,
      };
    }
  };
const validateProfessionalSellerShareholder = async (
  data: ProfessionalSellerShareholder,
  phonePrefix: string,
  tProf: TFunction
) => {
  try {
    await validateProfessionalSellerShareholderSchema(tProf).validate(data);
    const phoneNumber = parsePhoneNumberWithError(
      phonePrefix + data.phoneNumber
    );
    return {
      isValid: true && phoneNumber.isValid(),
      message: null,
    };
  } catch (err: any) {
    return {
      isValid: false,
      message: err.message,
    };
  }
};

const validateProfessionalSellerData = async (
  data: ProfessionalSellerDataClient & {
    primeBusinessContactPhonePrefix: string;
  },
  countriesAllowed: CommonCountryArray,
  tCommon: TFunction,
  tProf: TFunction
) => {
  try {
    await validateProfessionalSellerPersonalData(data, tProf);
  } catch (err: any) {
    return {
      isValid: false,
      message: err.message,
    };
  }
  try {
    await validateProfessionalSellerBusinessData(data, tProf);
  } catch (err: any) {
    return {
      isValid: false,
      message: err.message,
    };
  }

  for (const shareholder of data.shareholder) {
    try {
      await validateProfessionalSellerShareholder(
        shareholder,
        data.primeBusinessContactPhonePrefix,
        tProf
      );
    } catch (err: any) {
      return {
        isValid: false,
        message: err.message,
      };
    }
  }

  const countriesIso2 = countriesAllowed.map((c) => c.iso2);

  if (countriesAllowed.length === 0) {
    return {
      isValid: false,
      message: tCommon('an_unexpected_error_occurred'),
    };
  }

  if (!countriesIso2.includes(data.businessCountry)) {
    const translatedMessage = tCommon('prof_only_allowed');
    const countriesAllowedText = countriesAllowed.map((c) => c.name).join(', ');
    return {
      isValid: false,
      message: `${translatedMessage} ${countriesAllowedText}`,
    };
  }
  return {
    isValid: true,
    message: null,
  };
};

const validateUserData = async (data: UserDTO, phonePrefix: string) => {
  try {
    await validateUserSchema.validate(data);
    const phoneNumber = parsePhoneNumberWithError(
      phonePrefix + data.phoneNumber
    );

    return {
      isValid: true && phoneNumber.isValid(),
      message: null,
    };
  } catch (err: any) {
    return {
      isValid: false,
      message: err.message,
    };
  }
};

export {
  validateProfessionalSellerPersonalData,
  validateProfessionalSellerBusinessData,
  validateProfessionalSellerData,
  validateProfessionalSellerShareholder,
  validateUserData,
};
